/**
 * Background Sync Service
 * Automatically retries failed API operations from the sync queue
 */

import { getSyncQueue, removeFromSyncQueue, updateQueueItemAttempt } from './syncQueue';
import * as api from '../service/api';

const SYNC_INTERVAL = 30000; // Try to sync every 30 seconds
const MAX_QUEUE_ATTEMPTS = 10; // Maximum attempts per queued item

let syncIntervalId = null;
let isSyncing = false;

/**
 * Map of API call names to actual API functions
 */
const API_CALL_MAP = {
  'submitMovieSelection': api.submitMovieSelection,
  'submitGenrePreferences': api.submitGenrePreferences,
  'saveAttributePreferences': api.saveAttributePreferences,
  'submitPeoplePreferences': api.submitPeoplePreferences,
  'submitEmptyPeoplePreferences': api.submitEmptyPeoplePreferences,
  'submitReleaseYearPreferences': api.submitReleaseYearPreferences,
  'submitRecommendationSurvey': api.submitRecommendationSurvey,
  'saveMovieWeekSelections': api.saveMovieWeekSelections,
  'submitPairwiseAnswer': api.submitPairwiseAnswer,
  'submitFinalComparisonAnswer': api.submitFinalComparisonAnswer,
  'submitPostPairwiseSurvey': api.submitPostPairwiseSurvey,
  'submitPostStudySurvey': api.submitPostStudySurvey,
  'submitTrailerRating': api.submitTrailerRating,
  'saveFinalResponse': api.saveFinalResponse,
};

/**
 * Start background sync service
 */
export function startBackgroundSync() {
  if (syncIntervalId) {
    console.log('[SYNC] Background sync already running');
    return;
  }
  
  console.log('[SYNC] Starting background sync service');
  
  // Initial sync attempt
  syncQueuedItems();
  
  // Set up periodic sync
  syncIntervalId = setInterval(() => {
    syncQueuedItems();
  }, SYNC_INTERVAL);
}

/**
 * Stop background sync service
 */
export function stopBackgroundSync() {
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
    syncIntervalId = null;
    console.log('[SYNC] Background sync stopped');
  }
}

/**
 * Manually trigger sync of queued items
 */
export async function syncQueuedItems() {
  if (isSyncing) {
    console.log('[SYNC] Sync already in progress, skipping...');
    return { success: true, synced: 0, failed: 0 };
  }
  
  const queue = getSyncQueue();
  
  if (queue.length === 0) {
    return { success: true, synced: 0, failed: 0 };
  }
  
  isSyncing = true;
  console.log(`[SYNC] Syncing ${queue.length} queued items...`);
  
  let syncedCount = 0;
  let failedCount = 0;
  
  for (const item of queue) {
    // Skip if max attempts reached
    if (item.attempts >= MAX_QUEUE_ATTEMPTS) {
      console.warn(`[SYNC] Max attempts reached for ${item.type}, skipping...`);
      failedCount++;
      continue;
    }
    
    try {
      const apiFunction = API_CALL_MAP[item.apiCall];
      
      if (!apiFunction) {
        console.error(`[SYNC] Unknown API call: ${item.apiCall}`);
        removeFromSyncQueue(item.id);
        failedCount++;
        continue;
      }
      
      console.log(`[SYNC] Attempting to sync: ${item.type} (attempt ${item.attempts + 1})`);
      
      // Update attempt count
      updateQueueItemAttempt(item.id);
      
      // Try to execute the API call
      const result = await apiFunction(item.data);
      
      // Check if successful
      if (result === true || result?.success === true || result?.ok === true) {
        console.log(`[SYNC] Successfully synced: ${item.type}`);
        removeFromSyncQueue(item.id);
        syncedCount++;
      } else {
        console.warn(`[SYNC] Sync returned non-success for: ${item.type}`);
        failedCount++;
      }
      
    } catch (error) {
      console.error(`[SYNC] Failed to sync ${item.type}:`, error.message);
      failedCount++;
    }
  }
  
  isSyncing = false;
  
  const remainingQueue = getSyncQueue();
  if (remainingQueue.length > 0) {
    console.log(`[SYNC] ${remainingQueue.length} items still in queue`);
  } else {
    console.log('[SYNC] All items synced successfully!');
  }
  
  return {
    success: failedCount === 0,
    synced: syncedCount,
    failed: failedCount,
    remaining: remainingQueue.length
  };
}

/**
 * Force sync all queued items (blocking)
 * Used at critical checkpoints (e.g., study completion)
 */
export async function forceSyncAll(maxRetries = 3) {
  console.log('[SYNC] Force syncing all queued items...');
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await syncQueuedItems();
    
    if (result.remaining === 0) {
      console.log('[SYNC] All items force-synced successfully!');
      return { success: true, synced: result.synced };
    }
    
    if (attempt < maxRetries) {
      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      console.log(`[SYNC] ${result.remaining} items remaining, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  const queue = getSyncQueue();
  console.error(`[SYNC] Failed to sync ${queue.length} items after ${maxRetries} attempts`);
  
  return {
    success: false,
    remaining: queue.length,
    items: queue.map(item => item.type)
  };
}

/**
 * Check if sync is currently in progress
 */
export function isSyncInProgress() {
  return isSyncing;
}
